import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { RefreshCw, Store, Truck, User, Target } from 'lucide-react';
import { adminAPI } from '../../services/api';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    background-color: ${color};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 5px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

const storeIcon = createIcon('#f97316'); // Orange
const driverIcon = createIcon('#22c55e'); // Green
const buyerIcon = createIcon('#3b82f6'); // Blue

// Component to handle map center updates
const MapController = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

const AdminWarRoom = () => {
  const [nodes, setNodes] = useState({ stores: [], drivers: [], buyers: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [radius, setRadius] = useState(5);
  const [center, setCenter] = useState({ lat: 18.5141, lng: -88.3003 }); // Quintana Roo, Mexico
  const [density, setDensity] = useState(null);

  const loadNodes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/warroom/network-nodes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      const data = await response.json();
      setNodes(data);
      
      // Calculate density
      calculateDensity(center.lat, center.lng, radius);
    } catch (err) {
      console.error('Error loading nodes:', err);
    } finally {
      setLoading(false);
    }
  }, [center, radius]);

  const calculateDensity = useCallback(async (lat, lng, radiusKm) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/warroom/density?lat=${lat}&lng=${lng}&radius_km=${radiusKm}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      const data = await response.json();
      setDensity(data.density);
    } catch (err) {
      console.error('Error calculating density:', err);
    }
  }, []);

  useEffect(() => {
    loadNodes();
    // Refresh every 30 seconds
    const interval = setInterval(loadNodes, 30000);
    return () => clearInterval(interval);
  }, [loadNodes]);

  useEffect(() => {
    calculateDensity(center.lat, center.lng, radius);
  }, [center, radius, calculateDensity]);

  const handleMapClick = (e) => {
    setCenter({ lat: e.latlng.lat, lng: e.latlng.lng });
  };

  // Memoize all markers for performance
  const markers = useMemo(() => (
    <>
      {/* Store markers - Orange */}
      {nodes.stores.map((store) => (
        <Marker 
          key={`store-${store.id}`} 
          position={[store.lat, store.lng]}
          icon={storeIcon}
        >
          <Popup>
            <div className="text-sm">
              <p className="font-semibold text-orange-600">🏪 {store.name}</p>
              <p className="text-gray-500">Tienda activa</p>
            </div>
          </Popup>
        </Marker>
      ))}
      
      {/* Driver markers - Green */}
      {nodes.drivers.map((driver) => (
        <Marker 
          key={`driver-${driver.id}`} 
          position={[driver.lat, driver.lng]}
          icon={driverIcon}
        >
          <Popup>
            <div className="text-sm">
              <p className="font-semibold text-green-600">🏍️ {driver.name}</p>
              <p className="text-gray-500">Repartidor online</p>
              <p className="text-xs text-gray-400">{driver.vehicle}</p>
            </div>
          </Popup>
        </Marker>
      ))}
      
      {/* Buyer markers - Blue */}
      {nodes.buyers.map((buyer, idx) => (
        <Marker 
          key={`buyer-${buyer.id}-${idx}`} 
          position={[buyer.lat, buyer.lng]}
          icon={buyerIcon}
        >
          <Popup>
            <div className="text-sm">
              <p className="font-semibold text-blue-600">👤 Comprador</p>
              <p className="text-gray-500">Pedido reciente</p>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  ), [nodes]);

  return (
    <div className="space-y-4">
      {/* Density Panel - Floating */}
      <Card className="bg-gray-800 border-gray-700 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-500" />
              <span className="text-white font-semibold">War Room</span>
            </div>
            
            {density && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-400">Densidad en {radius} km:</span>
                <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded">
                  <Store className="w-4 h-4 inline mr-1" />
                  {density.stores} Comercios
                </span>
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded">
                  <Truck className="w-4 h-4 inline mr-1" />
                  {density.drivers} Moteros
                </span>
                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                  <User className="w-4 h-4 inline mr-1" />
                  {density.buyers} Compradores
                </span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* Radius Slider */}
            <div className="flex items-center gap-3 min-w-[200px]">
              <span className="text-gray-400 text-sm whitespace-nowrap">Radio:</span>
              <Slider
                value={[radius]}
                onValueChange={(v) => setRadius(v[0])}
                min={1}
                max={20}
                step={1}
                className="w-32"
              />
              <span className="text-white font-medium w-12">{radius} km</span>
            </div>
            
            <Button 
              onClick={loadNodes} 
              variant="outline" 
              size="sm"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-orange-500 border-2 border-white shadow"></div>
          <span className="text-gray-400">Tiendas ({nodes.stores.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow"></div>
          <span className="text-gray-400">Moteros Online ({nodes.drivers.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow"></div>
          <span className="text-gray-400">Compradores Recientes ({nodes.buyers.length})</span>
        </div>
      </div>

      {/* Map Container */}
      <Card className="bg-gray-800 border-gray-700 overflow-hidden" style={{ height: '500px' }}>
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          onClick={handleMapClick}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapController center={[center.lat, center.lng]} />
          
          {/* Radius Circle */}
          <Circle
            center={[center.lat, center.lng]}
            radius={radius * 1000}
            pathOptions={{
              color: '#f97316',
              fillColor: '#f97316',
              fillOpacity: 0.1,
              weight: 2,
              dashArray: '5, 5'
            }}
          />
          
          {/* Center Marker */}
          <Marker position={[center.lat, center.lng]}>
            <Popup>
              <div className="text-sm">
                <p className="font-semibold">📍 Centro de análisis</p>
                <p className="text-gray-500">Lat: {center.lat.toFixed(4)}</p>
                <p className="text-gray-500">Lng: {center.lng.toFixed(4)}</p>
              </div>
            </Popup>
          </Marker>
          
          {markers}
        </MapContainer>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-orange-500/10 border-orange-500/30 p-4 text-center">
          <Store className="w-8 h-8 text-orange-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{nodes.stats.total_stores || 0}</p>
          <p className="text-orange-400 text-sm">Comercios Activos</p>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30 p-4 text-center">
          <Truck className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{nodes.stats.total_drivers_online || 0}</p>
          <p className="text-green-400 text-sm">Moteros Online</p>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30 p-4 text-center">
          <User className="w-8 h-8 text-blue-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{nodes.stats.total_recent_buyers || 0}</p>
          <p className="text-blue-400 text-sm">Compradores (1h)</p>
        </Card>
      </div>
    </div>
  );
};

export default AdminWarRoom;
